/*
 * One GET, its loading and error state, and a way to run it again. Each
 * workspace loads its own data when it mounts; nothing is cached across pages,
 * so a page always shows what the server says now.
 */

import { useCallback, useEffect, useState, type DependencyList } from "react";
import { errorMessage } from "./client";

export type Load<T> = {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => void;
  /** Replace the data with a server response from a write, without a refetch. */
  setData: (next: T) => void;
};

export function useLoad<T>(fn: () => Promise<T>, deps: DependencyList = []): Load<T> {
  const [data, setDataState] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fn()
      .then((next) => {
        if (cancelled) return;
        setDataState(next);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(errorMessage(err, "This could not be loaded."));
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // `fn` is re-created every render; `deps` says when it asks something new.
  }, [...deps, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  const setData = useCallback((next: T) => setDataState(next), []);

  return { data, error, loading, reload, setData };
}
